import { Link } from "react-router-dom";
import { ArrowRight, Check, Shield, Layers, FileCheck2, Building2, Lock, Wallet, Database, Globe2 } from "lucide-react";
import { Seo } from "@/components/Seo";
import { MeyarLogo } from "@/components/MeyarLogo";
import { MeyarIntegrations } from "@/pages/inner/meyar/MeyarIntegrations";
import {
  ACCENT, DARK_BG, TINT_BG,
  FilledButton, OutlineButton, CircularProcessDiagram as SharedDiagram, AssuranceRow,
} from "@/pages/inner/meyar/_MeyarUI";

const FRAMEWORK_STRIP = [
  "ISO 27001", "SOC 2", "NIST CSF", "PCI DSS", "ISO 27701", "Oman PDPL",
];

const FRAMEWORK_TILES = [
  { icon: Shield, label: "ISO 27001" },
  { icon: FileCheck2, label: "SOC 2" },
  { icon: Layers, label: "NIST CSF" },
  { icon: Wallet, label: "PCI DSS" },
  { icon: Lock, label: "ISO 27701" },
  { icon: Building2, label: "ISO 22301" },
  { icon: Globe2, label: "Oman PDPL" },
  { icon: Database, label: "Your own framework" },
];

const PILLARS = [
  {
    n: "01",
    kicker: "Risk",
    title: "See what matters before it becomes urgent.",
    body: "Assessments, findings, exceptions and business context in one register, scored consistently instead of argued case by case.",
  },
  {
    n: "02",
    kicker: "Controls",
    title: "Map once. Prove across every framework.",
    body: "One control satisfies several standards at the same time, linked to the tests, evidence and policies behind it.",
    dark: true,
  },
  {
    n: "03",
    kicker: "Evidence",
    title: "Proof that stays ready.",
    body: "Collected from your systems as work happens, versioned, and kept with the test it belongs to. No screenshot hunting the week before an audit.",
    dark: true,
  },
  {
    n: "04",
    kicker: "Remediation",
    title: "Make secure the default outcome.",
    body: "Route work to the right owner, track progress, escalate what stalls, and close the loop with confidence.",
  },
];

const VANTAGE_POINTS = [
  {
    audience: "For security teams",
    headline: "Run the programme, not the spreadsheet.",
    body: "Coordinate controls, risk, owners, evidence, policies and remediation from one workspace.",
  },
  {
    audience: "For service providers",
    headline: "Deliver assurance at portfolio scale.",
    body: "Manage several client programmes with reusable playbooks, measurable service levels and shared reporting.",
    dark: true,
  },
  {
    audience: "For executives",
    headline: "Make cyber risk decision-ready.",
    body: "Translate technical work into exposure, maturity, accountability and the investment that matters most.",
  },
];

const ASSURANCE_ROWS = [
  {
    title: "Connected by design",
    body: "Risks, controls, evidence, policies and actions reference each other, so nothing sits in isolation.",
  },
  {
    title: "Accountability over activity",
    body: "Every task has an owner, a deadline and a record of what happened, not just a status.",
  },
  {
    title: "Built beyond the audit",
    body: "Compliance becomes a by-product of a programme that is genuinely operating, rather than the goal itself.",
  },
];

const GUIDANCE_ARTICLES = [
  {
    n: "01",
    category: "Foundations",
    title: "Continuous assurance versus point-in-time compliance.",
    desc: "Why a programme that runs all year costs less than one that restarts every audit cycle.",
    to: "/resources/blog/continuous-assurance-vs-point-in-time-compliance",
  },
  {
    n: "02",
    category: "Operations",
    title: "From finding to verified closure.",
    desc: "Building a remediation path that survives contact with a busy engineering team.",
    to: "/resources/blog/from-finding-to-verified-closure",
  },
  {
    n: "03",
    category: "For service providers",
    title: "Designing a repeatable multi-client assurance practice.",
    desc: "What to standardise, and what to leave flexible.",
    to: "/resources/blog/repeatable-multi-client-assurance-practice",
  },
];

// Local wrapper around the shared diagram — nodes + centre label are
// specific to the Cyber Security page.
function CircularProcessDiagram() {
  return (
    <SharedDiagram
      nodes={["Assess", "Prioritise", "Remediate", "Verify"]}
      centerLine1="ALWAYS"
      centerLine2="ASSURED"
      ariaLabel="Meyar continuous-assurance loop"
      testid="meyar-process-diagram"
    />
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function CyberSecurityGRC() {
  return (
    <div data-testid="meyar-cyber-grc-page" className="bg-white">
      <Seo
        title="Meyar — Cyber Security GRC"
        description="Meyar brings your risks, controls, evidence and remediation into one workspace. Move from cyber risk to verified trust."
      />

      {/* ================================================================
          SECTION 1 — HERO (dark band)
          ================================================================ */}
      <section
        data-testid="cyber-hero"
        style={{ backgroundColor: DARK_BG }}
        className="relative overflow-hidden text-white"
      >
        {/* Subtle radial highlight in the Meyar accent */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -end-40 -top-40 h-[520px] w-[520px] rounded-full opacity-25 blur-3xl"
          style={{ background: `radial-gradient(closest-side, ${ACCENT}, transparent 70%)` }}
        />

        <div className="relative mx-auto max-w-4xl px-4 pb-20 pt-24 text-center lg:px-6 lg:pt-32">
          <div className="mx-auto flex items-center justify-center gap-3">
            <MeyarLogo variant="knockout" className="h-6 md:h-7" />
            <span aria-hidden="true" className="h-4 w-px bg-white/30" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">Cyber Security GRC</span>
          </div>
          <h1 className="mt-4 font-heading text-4xl font-semibold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            Move from cyber risk to{" "}
            <span className="text-wafa-sunshine">verified trust.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/85 lg:text-lg">
            Meyar brings your risks, controls, evidence and remediation into one workspace. Your team sees what matters next, and can prove what is secure.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <FilledButton testid="hero-primary-cta">Book a walkthrough</FilledButton>
            <OutlineButton to="/meyar" tone="dark" testid="hero-secondary-cta">
              Explore the platform
            </OutlineButton>
          </div>
          <p className="mt-5 text-sm text-white/60">
            Built for security teams and service providers.
          </p>

          {/* Framework strip */}
          <div className="mt-16 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 border-t border-white/10 pt-6">
            {FRAMEWORK_STRIP.map((f, i) => (
              <span
                key={f}
                className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/45"
              >
                {f}
                {i < FRAMEWORK_STRIP.length - 1 ? <span className="ms-8 text-white/20">·</span> : null}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================
          SECTION 2 — STATEMENT (light, two columns)
          ================================================================ */}
      <section data-testid="cyber-statement" className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-24 lg:px-6 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-6">
              <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">
                Security work should create evidence, not more busywork.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:pt-4">
              <p className="max-w-xl text-base leading-relaxed text-wafa-ink/80 lg:text-lg">
                Every control you test, every risk you close and every policy you approve already produces proof. Meyar captures that proof as the work happens, so your team stops rebuilding the same answers before every audit.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          SECTION 3 — ACCOUNTABLE PATH (light, diagram + text)
          ================================================================ */}
      <section data-testid="cyber-accountable-path" style={{ backgroundColor: TINT_BG }}>
        <div className="mx-auto max-w-7xl px-4 py-24 lg:px-6 lg:py-32">
          <div className="grid gap-14 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-5">
              <CircularProcessDiagram />
            </div>
            <div className="lg:col-span-7">
              <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-wafa-deep sm:text-4xl">
                Turn every gap into an accountable path forward.
              </h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-wafa-ink/80 lg:text-lg">
                Most findings stop at the moment they are discovered. Meyar takes each one further — to a named owner, a deadline, a treatment plan and a record that it was actually closed.
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  "Every finding carries an owner, a due date and a next action",
                  "Controls map to the risks they reduce, and to every framework they satisfy",
                  "Evidence is preserved with a full, exportable audit trail",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <span
                      className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                      style={{ backgroundColor: ACCENT }}
                    >
                      <Check className="h-3 w-3 text-white" />
                    </span>
                    <span className="text-base text-wafa-ink/85 lg:text-lg">{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          SECTION 4 — FOUR PILLARS
          ================================================================ */}
      <section data-testid="cyber-pillars" className="bg-wafa-cream/40">
        <div className="mx-auto max-w-7xl px-4 py-24 lg:px-6 lg:py-32">
          <div className="max-w-3xl">
            <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">
              Everything your security programme needs to move.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-wafa-ink/80 lg:text-lg">
              Replace scattered spreadsheets, shared drives and email threads with one connected layer your whole team can work from.
            </p>
          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-2">
            {PILLARS.map((p) => {
              const dark = p.dark;
              return (
                <article
                  key={p.n}
                  data-testid={`pillar-${p.n}`}
                  className={`overflow-hidden rounded-2xl border p-8 transition-all hover:-translate-y-0.5 lg:p-10 ${
                    dark
                      ? "border-white/10 text-white"
                      : "border-wafa-deep/10 bg-white text-wafa-deep"
                  }`}
                  style={dark ? { backgroundColor: DARK_BG } : undefined}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div
                        className={`text-xs font-semibold uppercase tracking-[0.2em] ${
                          dark ? "text-white/60" : "text-wafa-primary"
                        }`}
                        style={dark ? { color: ACCENT } : undefined}
                      >
                        {p.n} · {p.kicker}
                      </div>
                      <h3
                        className={`mt-4 font-heading text-2xl font-semibold leading-snug sm:text-3xl ${
                          dark ? "text-white" : "text-wafa-deep"
                        }`}
                      >
                        {p.title}
                      </h3>
                      <p
                        className={`mt-4 max-w-md text-sm leading-relaxed lg:text-base ${
                          dark ? "text-white/80" : "text-wafa-ink/80"
                        }`}
                      >
                        {p.body}
                      </p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================================================================
          SECTION 5 — INTEGRATIONS (light)
          ================================================================ */}
      <section data-testid="cyber-integrations" className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-24 lg:px-6 lg:py-32">
          <div className="max-w-3xl">
            <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">
              Bring evidence and action together across your stack.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-wafa-ink/80 lg:text-lg">
              Connect the systems where infrastructure, identity, engineering and remediation work already live. Evidence arrives on its own, and actions go back out to the tools your teams already use.
            </p>
          </div>

          <div className="mt-14">
            <MeyarIntegrations tone="light" />
          </div>

          <p className="mt-8 text-sm text-wafa-ink/70">
            Plus an open API and webhooks, so your own systems can push evidence in directly.
          </p>

          <p className="mt-6 max-w-3xl text-[11px] leading-relaxed text-wafa-ink/55">
            All product names, logos and brands are the property of their respective owners. Their use here indicates compatibility only and does not imply endorsement or affiliation.
          </p>
        </div>
      </section>

      {/* ================================================================
          SECTION 6 — VANTAGE POINTS (light)
          ================================================================ */}
      <section data-testid="cyber-vantage-points" style={{ backgroundColor: TINT_BG }}>
        <div className="mx-auto max-w-7xl px-4 py-24 lg:px-6 lg:py-32">
          <div className="max-w-3xl">
            <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">
              One platform. Different points of view.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-wafa-ink/80 lg:text-lg">
              Meyar gives every stakeholder the detail they need, without maintaining separate versions of the truth.
            </p>
          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {VANTAGE_POINTS.map((v, i) => {
              const dark = v.dark;
              return (
                <article
                  key={v.audience}
                  data-testid={`vantage-${i}`}
                  className={`flex h-full flex-col rounded-2xl border p-8 transition-all hover:-translate-y-0.5 ${
                    dark ? "border-white/10 text-white" : "border-wafa-deep/10 bg-white text-wafa-deep"
                  }`}
                  style={dark ? { backgroundColor: DARK_BG } : undefined}
                >
                  <div
                    className="text-xs font-semibold uppercase tracking-[0.2em]"
                    style={{ color: dark ? "#8AD48A" : ACCENT }}
                  >
                    {v.audience}
                  </div>
                  <h3
                    className={`mt-4 font-heading text-2xl font-semibold leading-snug sm:text-3xl ${
                      dark ? "text-white" : "text-wafa-deep"
                    }`}
                  >
                    {v.headline}
                  </h3>
                  <p
                    className={`mt-5 text-sm leading-relaxed lg:text-base ${
                      dark ? "text-white/80" : "text-wafa-ink/80"
                    }`}
                  >
                    {v.body}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================================================================
          SECTION 7 — FRAMEWORKS (dark)
          ================================================================ */}
      <section
        data-testid="cyber-frameworks"
        style={{ backgroundColor: DARK_BG }}
        className="relative overflow-hidden text-white"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -start-40 top-1/2 h-[420px] w-[420px] -translate-y-1/2 rounded-full opacity-15 blur-3xl"
          style={{ background: `radial-gradient(closest-side, ${ACCENT}, transparent 70%)` }}
        />
        <div className="relative mx-auto max-w-7xl px-4 py-24 lg:px-6 lg:py-32">
          <div className="grid gap-14 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-5">
              <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                Grow into new requirements without rebuilding your programme.
              </h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-white/80 lg:text-lg">
                Start with the standards your customers and regulators expect today. Reuse the same controls and evidence as your business, your market and your obligations change.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {FRAMEWORK_TILES.map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    data-testid={`framework-tile-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="flex flex-col items-start gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-5 transition-colors hover:border-white/25 hover:bg-white/[0.06]"
                  >
                    <span
                      className="flex h-9 w-9 items-center justify-center rounded-md border border-white/15"
                      style={{ color: ACCENT }}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="text-sm font-semibold text-white/90">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          SECTION 8 — SOFTWARE AND JUDGEMENT (light, single column)
          ================================================================ */}
      <section data-testid="cyber-software-judgement" className="bg-white">
        <div className="mx-auto max-w-4xl px-4 py-24 lg:px-6 lg:py-32">
          <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">
            Software for the work. People for the judgement.
          </h2>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-wafa-ink/80 lg:text-lg">
            Technology removes repetition. It does not decide what is acceptable for your organisation. Meyar is built for advisory input, so the judgement calls sit with the people accountable for them.
          </p>
          <dl className="mt-8 grid gap-6 md:grid-cols-2">
            <div>
              <dt className="font-heading text-lg font-semibold text-wafa-deep">
                Guidance in context
              </dt>
              <dd className="mt-2 text-sm text-wafa-ink/80 lg:text-base">
                Practical prompts sit beside the control being tested, not in a separate manual.
              </dd>
            </div>
            <div>
              <dt className="font-heading text-lg font-semibold text-wafa-deep">
                Advice stays yours
              </dt>
              <dd className="mt-2 text-sm text-wafa-ink/80 lg:text-base">
                Your own advisers, auditors and consultants work inside the same system rather than alongside it.
              </dd>
            </div>
          </dl>
        </div>
      </section>

      {/* ================================================================
          SECTION 9 — ASSURANCE (light, accordion)
          ================================================================ */}
      <section data-testid="cyber-assurance" style={{ backgroundColor: TINT_BG }}>
        <div className="mx-auto max-w-5xl px-4 py-24 lg:px-6 lg:py-32">
          <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">
            Operate with assurance. Defend with confidence.
          </h2>
          <div className="mt-10 border-t border-wafa-deep/10">
            {ASSURANCE_ROWS.map((row, i) => (
              <AssuranceRow key={row.title} item={row} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================
          SECTION 10 — GUIDANCE ARTICLES
          ================================================================ */}
      <section data-testid="cyber-guidance-articles" className="bg-wafa-cream/40">
        <div className="mx-auto max-w-7xl px-4 py-24 lg:px-6 lg:py-32">
          <div className="max-w-3xl">
            <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">
              Practical guidance for assurance leaders.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-wafa-ink/80 lg:text-lg">
              Original, practical writing for teams building accountable security and assurance programmes.
            </p>
          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {GUIDANCE_ARTICLES.map((a) => (
              <article
                key={a.n}
                data-testid={`guidance-${a.n}`}
                className="flex h-full flex-col justify-between rounded-2xl border border-wafa-deep/10 bg-white p-7 transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(14,79,58,0.25)]"
              >
                <div>
                  <div className="flex items-baseline gap-3">
                    <span
                      className="font-heading text-3xl font-semibold"
                      style={{ color: ACCENT }}
                    >
                      {a.n}
                    </span>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-wafa-ink/60">
                      {a.category}
                    </span>
                  </div>
                  <h3 className="mt-5 font-heading text-xl font-semibold leading-snug text-wafa-deep sm:text-2xl">
                    {a.title}
                  </h3>
                  <p className="mt-4 text-sm text-wafa-ink/75 lg:text-base">{a.desc}</p>
                </div>
                <div className="mt-8">
                  <Link
                    to={a.to}
                    data-testid={`guidance-read-${a.n}`}
                    className="inline-flex items-center gap-2 rounded-md border border-wafa-primary px-4 py-2 text-sm font-semibold text-wafa-primary transition-colors hover:bg-wafa-primary hover:text-white"
                  >
                    Read <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================
          SECTION 11 — CLOSING CTA (dark)
          ================================================================ */}
      <section
        data-testid="cyber-closing-cta"
        style={{ backgroundColor: DARK_BG }}
        className="relative overflow-hidden text-white"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -end-32 bottom-0 h-[420px] w-[420px] rounded-full opacity-20 blur-3xl"
          style={{ background: `radial-gradient(closest-side, ${ACCENT}, transparent 70%)` }}
        />
        <div className="relative mx-auto max-w-7xl px-4 py-24 lg:px-6 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-6">
              <h2 className="font-heading text-4xl font-semibold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
                Know your risk.
                <br />
                <span className="text-wafa-sunshine">Prove your trust.</span>
              </h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-white/80 lg:text-lg">
                Bring cyber risk, compliance, evidence and remediation into one accountable operating system.
              </p>
              <div className="mt-8">
                <FilledButton testid="closing-cta">Book a walkthrough</FilledButton>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8">
                <div className="font-heading text-lg font-semibold text-white">
                  See how Meyar fits the way your team works
                </div>
                <ul className="mt-6 space-y-4">
                  {[
                    "Connect the controls and frameworks you already run",
                    "Import your existing risk register and evidence",
                    "Walk through a realistic scenario with your own data",
                  ].map((line) => (
                    <li key={line} className="flex items-start gap-3">
                      <span
                        className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                        style={{ backgroundColor: ACCENT }}
                      >
                        <Check className="h-3 w-3 text-white" />
                      </span>
                      <span className="text-sm text-white/85 lg:text-base">{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
